import { Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { StatusBadge } from '../shared/StatusBadge';
import { ProgressBar } from '../shared/ProgressBar';
import type { SimulationProgress as SimulationProgressType } from '../../../types/media-sim';

interface SimulationProgressProps {
  progress: SimulationProgressType;
  totalCostCents: number;
}

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatTimeRemaining(seconds: number | null): string {
  if (seconds === null) return 'Estimating...';
  if (seconds < 60) return `~${seconds}s remaining`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `~${minutes}m ${secs}s remaining`;
}

export function SimulationProgress({ progress, totalCostCents }: SimulationProgressProps) {
  const { theme } = useTheme();

  const overallPercent =
    progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  // Match the reflection sections' status subtitle for visual consistency.
  const statusLabel =
    progress.total > 0 && progress.completed >= progress.total
      ? 'Completed'
      : progress.completed > 0
        ? 'In progress...'
        : 'Pending';

  return (
    <Card variant="elevated" padding="sm">
      <Card.Header title="Article Generation" subtitle={statusLabel} />
      <Card.Body>
        <Stack gap="md">
          <Flex justify="between" align="center">
            <span style={{ fontSize: '0.9rem', color: theme.colors.text.primary }}>
              {progress.completed} / {progress.total} articles
              {progress.failed > 0 && (
                <span style={{ color: theme.colors.semantic.error.default }}>
                  {' '}({progress.failed} failed)
                </span>
              )}
            </span>
            <Flex gap="md" align="center">
              <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                {formatTimeRemaining(progress.estimated_seconds_remaining)}
              </span>
              <span
                style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}
                title="Estimated total for this execution — reflection notes plus article generation."
              >
                Est. total: {formatCost(totalCostCents)}
              </span>
            </Flex>
          </Flex>

          <ProgressBar value={overallPercent} size="md" showLabel />

          {progress.per_publication.map((pub) => {
            const pubPercent = pub.total > 0 ? Math.round((pub.completed / pub.total) * 100) : 0;
            return (
              <Card key={pub.publication_id} variant="outlined" padding="sm">
                <Card.Body>
                  <Flex align="center" gap="sm">
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: theme.colors.text.primary,
                        minWidth: 140,
                      }}
                    >
                      {pub.publisher}
                    </span>
                    <StatusBadge status={pub.current_stage} size="sm" />
                    <div style={{ flex: 1 }}>
                      <ProgressBar value={pubPercent} size="sm" showLabel />
                    </div>
                    <span style={{ fontSize: '0.8rem', color: theme.colors.text.secondary, minWidth: 50 }}>
                      {pub.completed}/{pub.total}
                    </span>
                  </Flex>
                </Card.Body>
              </Card>
            );
          })}
        </Stack>
      </Card.Body>
    </Card>
  );
}
